import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./discover-map.css";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Search, MapPin, Bed, Bath, Maximize2, Calendar, Plus, X, Loader2, TrendingUp, Home, Bell, Flame, AlertTriangle, Lock, DoorClosed, FileText, Users, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { DistressBadgeRow } from "@/components/DistressBadge";
import DiscoverInsights from "@/components/DiscoverInsights";
import StreetViewThumb from "@/components/StreetViewThumb";

// Build a coloured pin icon (green/yellow/red) using inline SVG so we don't need image assets.
function pinIcon(color) {
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
  <path d="M14 0C6.3 0 0 6.3 0 14c0 9.6 14 26 14 26s14-16.4 14-26C28 6.3 21.7 0 14 0z" fill="${color}"/>
  <circle cx="14" cy="14" r="5" fill="#fff"/>
</svg>`;
  return L.divIcon({
    html: svg,
    className: "af-pin",
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -36],
  });
}

const PIN_GREEN = pinIcon("#10B981");
const PIN_AMBER = pinIcon("#F59E0B");
const PIN_RED = pinIcon("#E76F51");
const PIN_BLUE = pinIcon("#3B82F6");

// Heuristic deal-score from RentCast list price vs. national-style median+sqft proxy.
function pinFor(listing) {
  const price = Number(listing.price || 0);
  const sqft = Number(listing.square_footage || 0);
  if (!price || !sqft) return PIN_BLUE;
  const ppsf = price / sqft;
  if (ppsf < 150) return PIN_GREEN;
  if (ppsf < 250) return PIN_AMBER;
  return PIN_RED;
}

function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center && Number.isFinite(center[0]) && Number.isFinite(center[1])) {
      map.flyTo(center, 11, { duration: 0.9 });
    }
  }, [center, map]);
  return null;
}

export default function Discover() {
  const nav = useNavigate();
  const [mode, setMode] = useState("area"); // "area" | "address"
  const [addressQuery, setAddressQuery] = useState("");
  const [addressLookingUp, setAddressLookingUp] = useState(false);
  const [form, setForm] = useState({ city: "Phoenix", state: "AZ", zip: "", price_min: "", price_max: "400000", beds_min: "3" });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // active listing for detail drawer
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // Operator-selected repair tier override for the Deal Signal card.
  // `null` = use the age-based auto heuristic; "Light" | "Medium" | "Heavy"
  // = stress-test the verdict against a manual scope guess.
  const [repairTierOverride, setRepairTierOverride] = useState(null);
  const [searched, setSearched] = useState(false);
  const [adding, setAdding] = useState(false);
  const [skipTracing, setSkipTracing] = useState(false);
  const [smsModal, setSmsModal] = useState(null); // { leadId, phones, chosenTo, body, sending }
  const [smsOpening, setSmsOpening] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  const listRef = useRef(null);

  // Hustler-tier distress filters. `status.enabled=false` means we render the
  // chips behind a lock icon → click prompts upgrade.
  const [distressStatus, setDistressStatus] = useState(null);
  const [distress, setDistress] = useState({
    absentee: false, foreclosure: false, tax_delinquent: false,
    vacant: false, probate: false, tired_landlord: false,
  });

  useEffect(() => {
    api.get("/distress/status")
      .then((r) => setDistressStatus(r.data))
      .catch(() => setDistressStatus({ enabled: false, tier: "free", attom_live: false }));
  }, []);

  const distressLocked = !distressStatus?.enabled;
  const toggleDistress = (key) => {
    if (distressLocked) {
      toast.info("Distress filters are a Hustler-tier feature.", {
        action: { label: "Upgrade", onClick: () => nav("/upgrade") },
      });
      return;
    }
    setDistress((d) => ({ ...d, [key]: !d[key] }));
  };

  const saveSearch = async () => {
    if (!form.city && !form.state && !form.zip) {
      toast.error("Enter at least a city, state, or ZIP first");
      return;
    }
    const defaultName = [form.city, form.state, form.zip].filter(Boolean).join(" ") +
      (form.beds_min ? ` ${form.beds_min}+bd` : "") +
      (form.price_min ? ` ≥${Number(form.price_min).toLocaleString()}` : "") +
      (form.price_max ? ` ≤${Number(form.price_max).toLocaleString()}` : "");
    const name = window.prompt("Name this saved search:", defaultName);
    if (!name) return;
    setSavingSearch(true);
    try {
      await api.post("/saved-searches", {
        name,
        city: form.city,
        state: form.state,
        zip_code: form.zip,
        price_min: form.price_min ? Number(form.price_min) : null,
        price_max: form.price_max ? Number(form.price_max) : null,
        beds_min: form.beds_min ? Number(form.beds_min) : null,
      });
      toast.success("Saved — we'll alert you when new GO deals appear.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSearch(false);
    }
  };

  const onSearch = async (e) => {
    e?.preventDefault();
    if (!form.city && !form.state && !form.zip) {
      toast.error("Enter at least a city, state, or ZIP");
      return;
    }
    setLoading(true);
    setSearched(true);
    setActive(null);
    setDetails(null);
    try {
      const params = {};
      if (form.city) params.city = form.city;
      if (form.state) params.state = form.state;
      if (form.zip) params.zip = form.zip;
      if (form.price_min) params.price_min = form.price_min;
      if (form.price_max) params.price_max = form.price_max;
      if (form.beds_min) params.beds_min = form.beds_min;
      if (!distressLocked) {
        if (distress.absentee) params.distress_absentee = true;
        if (distress.foreclosure) params.distress_foreclosure = true;
        if (distress.tax_delinquent) params.distress_tax_delinquent = true;
        if (distress.vacant) params.distress_vacant = true;
        if (distress.probate) params.distress_probate = true;
        if (distress.tired_landlord) params.distress_tired_landlord = true;
      }
      params.limit = 100;
      const { data } = await api.get("/discover/search", { params });
      setResults(data.results || []);
      if ((data.results || []).length === 0) {
        const wasDistress = (distress.absentee || distress.foreclosure || distress.tax_delinquent
                             || distress.vacant || distress.probate || distress.tired_landlord);
        toast.info(wasDistress
          ? "No distressed listings in this area. Try widening the filters or removing some distress signals."
          : "No listings matched — try widening your filters.");
      } else {
        toast.success(`${data.count} listings found${data.distress_filters_applied ? " (distress-filtered)" : ""}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  // Centre the map on the average lat/lng of results, or fall back to USA.
  const mapCenter = useMemo(() => {
    const withCoords = results.filter((r) => r.latitude && r.longitude);
    if (withCoords.length === 0) return [39.5, -98.35]; // US centre
    const lat = withCoords.reduce((s, r) => s + r.latitude, 0) / withCoords.length;
    const lng = withCoords.reduce((s, r) => s + r.longitude, 0) / withCoords.length;
    return [lat, lng];
  }, [results]);

  const openDetails = async (listing) => {
    setActive(listing);
    setDetails(null);
    setLoadingDetails(true);
    try {
      const { data } = await api.get("/discover/property", { params: { address: listing.address } });
      setDetails(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load property details");
    } finally {
      setLoadingDetails(false);
    }
  };

  // Direct single-address lookup — skips the listings search and pops the property
  // detail panel straight from RentCast so users can price up an off-market lead.
  const lookupAddress = async (e) => {
    e?.preventDefault();
    const addr = addressQuery.trim();
    if (addr.length < 6) {
      toast.error("Enter a full property address (e.g. 123 Main St, Phoenix, AZ 85003)");
      return;
    }
    setAddressLookingUp(true);
    setSearched(true);
    setResults([]);
    setActive(null);
    setDetails(null);
    try {
      const { data } = await api.get("/discover/property", { params: { address: addr } });
      if (!data || !data.property) {
        toast.info("No RentCast record for that address — try including city + state + ZIP.");
        return;
      }
      const p = data.property;
      // Prefer the ACTUAL live MLS list price if RentCast /listings/sale has
      // this address as an active listing. Otherwise fall back to the AVM
      // (Automated Valuation Model) estimate. These are TWO DIFFERENT signals:
      //  • active_listing.price -> real current list price ($59,900 for 2009 W Indiana)
      //  • value.price           -> RentCast AVM estimate ($116,000)
      // Historically we only surfaced the AVM as `price` which caused the
      // "$119K vs $59.9K" investor complaint. Fixed 2026-02.
      const activeList = data.active_listing || null;
      const avm = data.value || null;
      const livePrice = activeList?.price ?? null;
      const avmPrice = avm ? (avm.price ?? avm.value) : null;
      // Synthesize a listing-shaped object so the existing detail panel + add-to-pipeline
      // flow (which reads from `active.address`, `active.city`, etc.) works unchanged.
      const listing = {
        id: p.id || addr,
        address: p.address || addr,
        city: p.city || "",
        state: p.state || "",
        zip_code: p.zip_code || "",
        latitude: p.latitude || null,
        longitude: p.longitude || null,
        bedrooms: p.bedrooms ?? null,
        bathrooms: p.bathrooms ?? null,
        square_footage: p.square_footage ?? null,
        year_built: p.year_built ?? null,
        property_type: p.property_type || null,
        // `price` is now DEFINITIVELY the current list price (if active)
        // or the AVM estimate (fallback). Sources tracked on the listing so
        // downstream calculators know which number they're working with.
        price: livePrice ?? avmPrice ?? null,
        price_source: livePrice != null ? "mls_active" : (avmPrice != null ? "avm_estimate" : "unknown"),
        avm_price: avmPrice,
        active_list_price: livePrice,
        active_listing_status: activeList?.status || null,
        active_days_on_market: activeList?.days_on_market ?? null,
        active_mls_name: activeList?.mls_name || null,
        listed_date: activeList?.listed_date || null,
        mls_name: activeList?.mls_name || "Address lookup",
      };
      setResults([listing]);
      setActive(listing);
      setDetails(data);
      if (livePrice != null && avmPrice != null && Math.abs(livePrice - avmPrice) / avmPrice > 0.15) {
        toast.success(
          `Property loaded · Live list $${livePrice.toLocaleString()} (AVM est. $${avmPrice.toLocaleString()})`
        );
      } else if (livePrice != null) {
        toast.success(`Property loaded · Currently listed at $${livePrice.toLocaleString()}`);
      } else {
        toast.success("Property loaded from RentCast (AVM estimate — no active listing found)");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Address lookup failed");
    } finally {
      setAddressLookingUp(false);
    }
  };

  const addToPipeline = async () => {
    if (!active) return;
    // If already in pipeline, skip the POST and navigate directly.
    if (details?.in_pipeline_lead_id) {
      nav(`/app/leads/${details.in_pipeline_lead_id}/report`);
      return;
    }
    setAdding(true);
    try {
      const payload = {
        address: active.address,
        city: active.city,
        state: active.state,
        zip_code: active.zip_code,
        bedrooms: active.bedrooms,
        bathrooms: active.bathrooms,
        sqft: active.square_footage,
        asking_price: active.price,
        estimated_arv: details?.value?.price ?? null,
        estimated_repairs: 0,
        notes: `Imported from Discover • ${active.mls_name || "MLS"} • Listed ${active.listed_date?.slice(0, 10) || "n/a"}`,
      };
      const { data } = await api.post("/discover/add-to-pipeline", payload);
      toast.success(data.created ? "Added to pipeline" : "Already in your pipeline");
      if (data?.lead?.id) {
        setTimeout(() => nav(`/app/leads/${data.lead.id}/report`), 600);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add to pipeline");
    } finally {
      setAdding(false);
    }
  };

  /**
   * Skip-trace the current property's owner in one click.
   *
   * Skip trace is lead-scoped in our system, so if the property isn't already
   * in the user's pipeline we hydrate it first, then chain the trace, then
   * drop the operator onto the lead report so they see the freshly-populated
   * phones + emails.
   */
  const runSkipTraceOnOwner = async () => {
    if (!active) return;
    setSkipTracing(true);
    try {
      let leadId = details?.in_pipeline_lead_id;
      if (!leadId) {
        const payload = {
          address: active.address,
          city: active.city,
          state: active.state,
          zip_code: active.zip_code,
          bedrooms: active.bedrooms,
          bathrooms: active.bathrooms,
          sqft: active.square_footage,
          asking_price: active.price,
          estimated_arv: details?.value?.price ?? null,
          estimated_repairs: 0,
          notes: `Imported from Discover for skip-trace • ${active.mls_name || "MLS"}`,
        };
        const { data } = await api.post("/discover/add-to-pipeline", payload);
        leadId = data?.lead?.id;
        if (!leadId) {
          toast.error("Couldn't add this property to your pipeline — try Run Full Report first.");
          return;
        }
      }
      const { data: trace } = await api.post(`/skip-trace/leads/${leadId}`);
      const phones = trace?.result?.phones?.length || 0;
      const emails = trace?.result?.emails?.length || 0;
      toast.success(`Skip trace complete — ${phones} phone${phones === 1 ? "" : "s"}, ${emails} email${emails === 1 ? "" : "s"} 📞`);
      setTimeout(() => nav(`/app/leads/${leadId}/report`), 700);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 || err?.response?.status === 403) {
        toast.error(detail || "Skip-trace quota exceeded — top up or upgrade to continue.", {
          action: { label: "Upgrade", onClick: () => nav("/upgrade") },
        });
      } else {
        toast.error(detail || "Skip trace failed");
      }
    } finally {
      setSkipTracing(false);
    }
  };

  /**
   * Compose a friendly SMS opener personalized to the property + owner.
   *
   * We intentionally keep it under 160 chars and TCPA-safe: no "buy your house
   * now" pressure, includes a brief opt-out cue. The operator can edit before
   * sending.
   */
  const buildSellerSmsBody = () => {
    const owner = details?.property?.owner?.names?.[0] || "";
    const first = (owner || "").trim().split(/\s+/)[0] || "there";
    const short = (active?.address || "").split(",")[0] || "your property";
    return `Hey ${first}, I saw ${short} — I'm a local investor and might be interested. Any chance you'd consider a cash offer? Reply STOP to opt out.`;
  };

  /**
   * "Text this seller" one-tap flow.
   *
   * If we don't have phones for this owner yet we run a skip trace inline to
   * fetch them (chained through add-to-pipeline first if the property isn't a
   * lead yet). Then we open a lightweight compose modal seeded with the
   * highest-confidence non-DNC number and a friendly, TCPA-safe opener.
   */
  const openTextSellerModal = async () => {
    if (!active) return;
    setSmsOpening(true);
    try {
      let leadId = details?.in_pipeline_lead_id;
      let phones = [];
      if (!leadId) {
        const payload = {
          address: active.address,
          city: active.city,
          state: active.state,
          zip_code: active.zip_code,
          bedrooms: active.bedrooms,
          bathrooms: active.bathrooms,
          sqft: active.square_footage,
          asking_price: active.price,
          estimated_arv: details?.value?.price ?? null,
          estimated_repairs: 0,
          notes: `Imported from Discover for outreach • ${active.mls_name || "MLS"}`,
        };
        const { data } = await api.post("/discover/add-to-pipeline", payload);
        leadId = data?.lead?.id;
        if (!leadId) {
          toast.error("Couldn't add this property to your pipeline.");
          return;
        }
      }
      // Try to reuse phones from the most recent skip trace on this lead — if
      // none exist, run a fresh trace so the operator has real numbers to text.
      try {
        const { data } = await api.get(`/skip-trace/history/${leadId}`);
        const latest = (data?.traces || [])[0];
        phones = latest?.result?.phones || [];
      } catch { /* no prior trace */ }

      if (phones.length === 0) {
        const { data: trace } = await api.post(`/skip-trace/leads/${leadId}`);
        phones = trace?.result?.phones || [];
      }

      const usablePhones = phones.filter((p) => !p.dnc);
      if (usablePhones.length === 0) {
        if (phones.length > 0) {
          toast.error("Only DNC numbers on file for this owner — texting blocked by TCPA.");
        } else {
          toast.error("No phones found for this owner — try skip trace again later.");
        }
        return;
      }
      const top = usablePhones[0];
      setSmsModal({
        leadId,
        phones: usablePhones,
        chosenTo: top.number,
        body: buildSellerSmsBody(),
        sending: false,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 || err?.response?.status === 403) {
        toast.error(detail || "Skip-trace quota exceeded — top up or upgrade to text sellers.", {
          action: { label: "Upgrade", onClick: () => nav("/upgrade") },
        });
      } else {
        toast.error(detail || "Couldn't prepare seller text");
      }
    } finally {
      setSmsOpening(false);
    }
  };

  const sendSellerSms = async () => {
    if (!smsModal) return;
    if (!smsModal.body.trim()) {
      toast.error("Message can't be empty");
      return;
    }
    setSmsModal((m) => ({ ...m, sending: true }));
    try {
      await api.post("/twilio/send", {
        lead_id: smsModal.leadId,
        to: smsModal.chosenTo,
        body: smsModal.body.trim(),
      });
      toast.success("Text sent — check the lead thread for replies 📩");
      setSmsModal(null);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // A2P 10DLC gate returns a structured error with a fix_url.
      if (status === 412 && detail && typeof detail === "object" && detail.fix_url) {
        toast.error(detail.message || "SMS blocked — finish Twilio A2P setup first.", {
          action: { label: "Open Settings", onClick: () => nav(detail.fix_url) },
        });
      } else if (status === 402 || status === 403) {
        toast.error(typeof detail === "string" ? detail : "SMS blocked — upgrade required.", {
          action: { label: "Upgrade", onClick: () => nav("/upgrade") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to send text");
      }
      setSmsModal((m) => (m ? { ...m, sending: false } : m));
    }
  };

  return (
    <div data-testid="discover-page">
      <div className="topbar">
        <div>
          <div className="crumb">Discover</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Search the Market</h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            Live property listings with map pins. One click hydrates a full lead into your pipeline.
          </p>
        </div>
      </div>

      {/* Search mode toggle — Area vs. Single Address */}
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="discover-mode-toggle">
        <button
          type="button"
          onClick={() => setMode("area")}
          className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition flex items-center gap-1.5 ${mode === "area" ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--ink-3)]"}`}
          data-testid="discover-mode-area"
        >
          <MapPin size={12} /> Search an area
        </button>
        <button
          type="button"
          onClick={() => setMode("address")}
          className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition flex items-center gap-1.5 ${mode === "address" ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--ink-3)]"}`}
          data-testid="discover-mode-address"
        >
          <Home size={12} /> Look up a single address
        </button>
      </div>

      {/* Single-address lookup form */}
      {mode === "address" && (
        <form
          onSubmit={lookupAddress}
          className="card p-3 mb-4 flex flex-col md:flex-row gap-2 items-stretch md:items-end"
          data-testid="discover-address-form"
        >
          <label className="flex-1 text-xs font-semibold text-[var(--ink-2)]">
            Property address
            <div className="mt-1">
              <AddressAutocomplete
                name="discover_address"
                placeholder="123 Main St, Phoenix, AZ 85003"
                inputClassName="input"
                dataTestid="discover-input-address"
                value={addressQuery}
                onChange={setAddressQuery}
                onSelect={setAddressQuery}
              />
            </div>
          </label>
          <button
            type="submit"
            disabled={addressLookingUp}
            className="btn btn-primary"
            data-testid="discover-address-lookup-btn"
          >
            {addressLookingUp ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {addressLookingUp ? "Looking up…" : "Look up property"}
          </button>
        </form>
      )}

      {/* search bar */}
      {mode === "area" && (
      <form
        onSubmit={onSearch}
        className="card p-3 mb-4 grid grid-cols-2 lg:grid-cols-7 gap-2 items-end"
        data-testid="discover-search-form"
      >
        <label className="col-span-2 lg:col-span-2 text-xs text-[var(--ink-2)]">
          City
          <input
            type="text"
            className="input mt-1"
            placeholder="Phoenix"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            data-testid="discover-input-city"
          />
        </label>
        <label className="text-xs text-[var(--ink-2)]">
          State
          <input
            type="text"
            maxLength={2}
            className="input mt-1 uppercase"
            placeholder="AZ"
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
            data-testid="discover-input-state"
          />
        </label>
        <label className="text-xs text-[var(--ink-2)]">
          ZIP
          <input
            type="text"
            className="input mt-1"
            placeholder="85024"
            value={form.zip}
            onChange={(e) => setForm({ ...form, zip: e.target.value })}
            data-testid="discover-input-zip"
          />
        </label>
        <label className="text-xs text-[var(--ink-2)]">
          Min Price
          <input
            type="number"
            className="input mt-1"
            placeholder="50000"
            value={form.price_min}
            onChange={(e) => setForm({ ...form, price_min: e.target.value })}
            data-testid="discover-input-price-min"
          />
        </label>
        <label className="text-xs text-[var(--ink-2)]">
          Max Price
          <input
            type="number"
            className="input mt-1"
            placeholder="400000"
            value={form.price_max}
            onChange={(e) => setForm({ ...form, price_max: e.target.value })}
            data-testid="discover-input-price-max"
          />
        </label>
        <label className="text-xs text-[var(--ink-2)]">
          Min Beds
          <input
            type="number"
            className="input mt-1"
            placeholder="3"
            value={form.beds_min}
            onChange={(e) => setForm({ ...form, beds_min: e.target.value })}
            data-testid="discover-input-beds-min"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary col-span-1"
          data-testid="discover-search-btn"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
          {loading ? "Searching…" : "Search"}
        </button>
        <button
          type="button"
          disabled={savingSearch || loading}
          onClick={saveSearch}
          className="btn btn-ghost col-span-1"
          title="Save these filters — we'll alert you when new matching listings hit the market."
          data-testid="discover-save-search-btn"
        >
          {savingSearch ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />} Save
        </button>
      </form>
      )}

      {/* Distress filters — Hustler+ only */}
      {mode === "area" && (
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="discover-distress-filters">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-2)] mr-1">
          Distress filters
          {distressLocked && (
            <Lock size={11} className="inline ml-1 -mt-0.5 text-amber-600" title="Hustler-tier feature" />
          )}
        </span>
        <DistressChip
          active={distress.foreclosure}
          locked={distressLocked}
          onClick={() => toggleDistress("foreclosure")}
          icon={Flame}
          label="Pre-Foreclosure"
          testid="discover-distress-foreclosure"
          activeBg="bg-red-100"
          activeText="text-red-800"
          activeBorder="border-red-300"
        />
        <DistressChip
          active={distress.tax_delinquent}
          locked={distressLocked}
          onClick={() => toggleDistress("tax_delinquent")}
          icon={AlertTriangle}
          label="Tax-Delinquent"
          testid="discover-distress-tax"
          activeBg="bg-amber-100"
          activeText="text-amber-800"
          activeBorder="border-amber-300"
        />
        <DistressChip
          active={distress.absentee}
          locked={distressLocked}
          onClick={() => toggleDistress("absentee")}
          icon={Home}
          label="Absentee Owner"
          testid="discover-distress-absentee"
          activeBg="bg-sky-100"
          activeText="text-sky-800"
          activeBorder="border-sky-300"
        />
        <DistressChip
          active={distress.vacant}
          locked={distressLocked}
          onClick={() => toggleDistress("vacant")}
          icon={DoorClosed}
          label="Vacant"
          testid="discover-distress-vacant"
          activeBg="bg-violet-100"
          activeText="text-violet-800"
          activeBorder="border-violet-300"
        />
        <DistressChip
          active={distress.probate}
          locked={distressLocked}
          onClick={() => toggleDistress("probate")}
          icon={FileText}
          label="Probate"
          testid="discover-distress-probate"
          activeBg="bg-indigo-100"
          activeText="text-indigo-800"
          activeBorder="border-indigo-300"
        />
        <DistressChip
          active={distress.tired_landlord}
          locked={distressLocked}
          onClick={() => toggleDistress("tired_landlord")}
          icon={Users}
          label="Tired Landlord"
          testid="discover-distress-tired-landlord"
          activeBg="bg-rose-100"
          activeText="text-rose-800"
          activeBorder="border-rose-300"
        />
        {distressLocked && (
          <button
            type="button"
            onClick={() => nav("/upgrade")}
            className="text-xs font-semibold text-emerald-700 underline ml-1 hover:text-emerald-900"
            data-testid="discover-distress-upgrade-cta"
          >
            Unlock with Hustler →
          </button>
        )}
        {!distressLocked && distressStatus && distressStatus.distress_live === false && !distressStatus.attom_live && (
          <span className="text-[10px] text-[var(--ink-3)] ml-1" data-testid="discover-distress-mock-banner">
            (demo data — live vendor not connected)
          </span>
        )}
      </div>
      )}

      {/* list + map */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* listings panel */}
        <div className="lg:col-span-2 max-h-[72vh] overflow-y-auto pr-1" ref={listRef} data-testid="discover-listings">
          {!searched && (
            <>
              <div className="card p-6 text-center text-[var(--ink-2)]" data-testid="discover-empty-state">
                <Home size={28} className="mx-auto mb-2 text-[var(--primary)]" />
                <div className="font-semibold text-[var(--ink)] mb-1">Find your next deal</div>
                <div className="text-sm">Enter a city, state, or ZIP and we&rsquo;ll plot live listings on the map.</div>
              </div>
              <DiscoverInsights
                onPickMarket={(picked) => {
                  setForm((f) => ({ ...f, ...picked }));
                  // Auto-fire the search a beat later so React commits state.
                  setTimeout(() => onSearch(), 50);
                }}
              />
            </>
          )}
          {searched && !loading && results.length === 0 && (
            <div className="card p-6 text-center text-[var(--ink-2)]" data-testid="discover-no-results">
              No listings matched. Try a different city or widen your filters.
            </div>
          )}
          <div className="flex flex-col gap-2">
            {results.map((r) => {
              const isActive = active?.id === r.id;
              const ppsf = r.price && r.square_footage ? r.price / r.square_footage : null;
              return (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => openDetails(r)}
                  className={"text-left card p-3 transition " + (isActive ? "border-[var(--primary)] shadow-md" : "hover:border-[var(--ink-2)]")}
                  data-testid={`discover-listing-card-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate" data-testid="discover-listing-address">{r.address}</div>
                      <div className="text-xs text-[var(--ink-2)] mt-0.5">
                        {r.property_type || "Property"} • {r.year_built ? `Built ${r.year_built}` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bold text-[var(--primary)]">{fmtMoney(r.price)}</div>
                      {ppsf && (
                        <div className="text-[10px] text-[var(--ink-2)]">${Math.round(ppsf)}/sqft</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-[var(--ink-2)] flex-wrap">
                    {r.bedrooms != null && (
                      <span className="flex items-center gap-1"><Bed size={12} /> {r.bedrooms} bd</span>
                    )}
                    {r.bathrooms != null && (
                      <span className="flex items-center gap-1"><Bath size={12} /> {r.bathrooms} ba</span>
                    )}
                    {r.square_footage != null && (
                      <span className="flex items-center gap-1"><Maximize2 size={12} /> {r.square_footage.toLocaleString()} sqft</span>
                    )}
                    {r.days_on_market != null && (
                      <span className="flex items-center gap-1"><Calendar size={12} /> {r.days_on_market} DOM</span>
                    )}
                  </div>
                  {r.distress && (r.distress.foreclosure || r.distress.tax_delinquent || r.distress.absentee || r.distress.vacant || r.distress.probate || r.distress.tired_landlord) && (
                    <div className="mt-2">
                      <DistressBadgeRow signal={r.distress} compact />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* map */}
        <div className="lg:col-span-3 card p-0 overflow-hidden" data-testid="discover-map-wrap" style={{ height: "72vh" }}>
          <MapContainer
            center={mapCenter}
            zoom={results.length ? 11 : 4}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapRecenter center={mapCenter} />
            {results.map((r) =>
              r.latitude && r.longitude ? (
                <Marker
                  key={r.id}
                  position={[r.latitude, r.longitude]}
                  icon={pinFor(r)}
                  eventHandlers={{ click: () => openDetails(r) }}
                >
                  {/* Hover-only preview: address + one-line stats so the
                      operator can scan the map without clicking every pin. */}
                  <Tooltip
                    direction="top" offset={[0, -12]} opacity={1}
                    sticky permanent={false}
                    className="discover-hover-tooltip"
                  >
                    <div className="text-[11.5px] leading-tight">
                      <div className="font-bold text-slate-900">
                        {r.address || "Unaddressed property"}
                      </div>
                      <div className="text-slate-600 mt-0.5">
                        {[fmtMoney(r.price),
                          r.bedrooms ? `${r.bedrooms}bd` : null,
                          r.bathrooms ? `${r.bathrooms}ba` : null,
                          r.square_footage ? `${r.square_footage.toLocaleString()} sqft` : null]
                          .filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                  </Tooltip>
                  <Popup>
                    <div className="text-xs">
                      <div className="font-semibold">{r.address}</div>
                      <div className="mt-1">{fmtMoney(r.price)} • {r.bedrooms || "?"}bd / {r.bathrooms || "?"}ba • {r.square_footage?.toLocaleString() || "?"} sqft</div>
                    </div>
                  </Popup>
                </Marker>
              ) : null,
            )}
          </MapContainer>
        </div>
      </div>

      {/* property detail drawer */}
      {active && (
        <div
          className="fixed inset-0 bg-black/40 z-[1000] flex justify-end"
          onClick={() => { setActive(null); setDetails(null); setRepairTierOverride(null); }}
          data-testid="discover-detail-overlay"
        >
          <aside
            className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="discover-detail-drawer"
          >
            <div className="flex items-center justify-between p-4 border-b">
              <div className="min-w-0">
                <div className="text-xs text-[var(--ink-2)] flex items-center gap-1"><MapPin size={12} /> {active.city}, {active.state}</div>
                <div className="font-semibold truncate">{active.address}</div>
              </div>
              <button
                type="button"
                className="btn btn-ghost p-2"
                onClick={() => { setActive(null); setDetails(null); setRepairTierOverride(null); }}
                data-testid="discover-detail-close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <StreetViewThumb
                address={[active.address, active.city, active.state, active.zip_code].filter(Boolean).join(", ")}
                alt={`Street view of ${active.address}`}
                className="w-full h-44 rounded-lg border border-[var(--line)]"
              />

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="card p-3" data-testid="discover-detail-list-price">
                  <div className="text-xs text-[var(--ink-2)] flex items-center gap-1">
                    List Price
                    {active.price_source === "mls_active" && (
                      <span className="ml-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800" data-testid="discover-price-source-mls">
                        LIVE MLS
                      </span>
                    )}
                    {active.price_source === "avm_estimate" && (
                      <span className="ml-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="discover-price-source-avm">
                        AVM EST.
                      </span>
                    )}
                  </div>
                  <div className="font-bold text-lg">{fmtMoney(active.price)}</div>
                  {active.price_source === "mls_active" && active.avm_price != null && (
                    <div className="text-[10px] text-[var(--ink-2)] mt-0.5">
                      AVM est.: {fmtMoney(active.avm_price)}
                    </div>
                  )}
                  {active.price_source === "avm_estimate" && (
                    <div className="text-[10px] text-amber-700 mt-0.5">
                      Not on the market — this is RentCast&apos;s valuation.
                    </div>
                  )}
                </div>
                <div className="card p-3">
                  <div className="text-xs text-[var(--ink-2)]">Days on Market</div>
                  <div className="font-bold text-lg">
                    {active.active_days_on_market ?? active.days_on_market ?? "—"}
                  </div>
                  {active.active_mls_name && (
                    <div className="text-[10px] text-[var(--ink-2)] mt-0.5 truncate">
                      via {active.active_mls_name}
                    </div>
                  )}
                </div>
                <div className="card p-3 col-span-2 flex items-center justify-around text-xs text-[var(--ink-2)]">
                  <span className="flex items-center gap-1"><Bed size={14} /> {active.bedrooms ?? "—"} bd</span>
                  <span className="flex items-center gap-1"><Bath size={14} /> {active.bathrooms ?? "—"} ba</span>
                  <span className="flex items-center gap-1"><Maximize2 size={14} /> {active.square_footage?.toLocaleString() ?? "—"} sqft</span>
                </div>
              </div>

              {loadingDetails && (
                <div className="card p-3 text-sm text-[var(--ink-2)] flex items-center gap-2" data-testid="discover-details-loading">
                  <Loader2 size={14} className="animate-spin" /> Loading AVM, comps, and owner data…
                </div>
              )}

              {details?.value && (
                <div className="card p-3" data-testid="discover-detail-deal-signal">
                  <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide flex items-center gap-1"><TrendingUp size={12} /> Deal Signal</div>
                  {(() => {
                    const arv = Number(details.value.price || 0);
                    const ask = Number(active.price || 0);
                    const sqft = Number(active.square_footage || 0);
                    const yearBuilt = Number(active.year_built || 0);
                    if (!arv || !ask) {
                      return (
                        <div className="text-xs text-[var(--ink-2)] mt-2">
                          AVM unavailable — run the full report for AI-grade analysis.
                        </div>
                      );
                    }
                    // Repair tier heuristic from age:
                    //   <15 yrs → light ($15/sqft)
                    //   15-40 yrs → medium ($25/sqft)
                    //   >40 yrs → heavy ($40/sqft)
                    // Operator can override via the Light/Medium/Heavy chips
                    // below to stress-test the verdict against their own scope.
                    const age = yearBuilt ? new Date().getFullYear() - yearBuilt : 25;
                    let repairTier, repairPsf;
                    if (repairTierOverride) {
                      repairTier = repairTierOverride;
                      repairPsf = repairTierOverride === "Light" ? 15 : repairTierOverride === "Medium" ? 25 : 40;
                    } else if (age < 15)      { repairTier = "Light";  repairPsf = 15; }
                    else if (age < 40) { repairTier = "Medium"; repairPsf = 25; }
                    else               { repairTier = "Heavy";  repairPsf = 40; }
                    const isTierAuto = !repairTierOverride;
                    const repairEst = sqft ? sqft * repairPsf : Math.round(arv * 0.08);
                    const mao = arv * 0.70 - repairEst - 10000;
                    const spread = mao - ask;
                    const discountPct = ((arv - ask) / arv) * 100;

                    // Verdict — refined 2026-02 to prevent "PASS" from firing
                    // on massively-discounted deals just because the strict
                    // 70% rule + assumed heavy rehab doesn't clear. At 30%+
                    // discount to ARV there's almost always a wholesale
                    // exit (novation, subject-to, creative finance) so we
                    // never scare the operator away from an obvious deal.
                    let verdict, verdictColor, verdictReason;
                    if (discountPct >= 40) {
                      verdict = "GO";
                      verdictColor = "bg-emerald-100 text-emerald-900 border-emerald-300";
                      verdictReason = spread > 0
                        ? `${Math.round(discountPct)}% below ARV — hard-money exit clears.`
                        : `${Math.round(discountPct)}% below ARV — massive discount. If strict 70% rule doesn't clear, novation / creative structure will.`;
                    } else if (spread > 0 && discountPct >= 25) {
                      verdict = "GO";
                      verdictColor = "bg-emerald-100 text-emerald-900 border-emerald-300";
                      verdictReason = `${Math.round(discountPct)}% below ARV with positive spread after ${repairTier.toLowerCase()} rehab.`;
                    } else if (discountPct >= 25 || spread > -15000) {
                      verdict = "MAYBE";
                      verdictColor = "bg-amber-100 text-amber-900 border-amber-300";
                      verdictReason = spread > 0
                        ? "Positive spread, but discount is thin — negotiate hard on repair credits."
                        : `Tight margin at ${repairTier.toLowerCase()} rehab. Verify actual scope or push ask down $${Math.abs(Math.round(spread / 1000))}K.`;
                    } else if (discountPct >= 10) {
                      verdict = "MAYBE";
                      verdictColor = "bg-amber-100 text-amber-900 border-amber-300";
                      verdictReason = "Some discount, but MAO doesn't clear after assumed repairs — creative finance or lower ask needed.";
                    } else {
                      verdict = "PASS";
                      verdictColor = "bg-rose-100 text-rose-900 border-rose-300";
                      verdictReason = "Priced at or above ARV — no wholesale angle without a huge repair credit.";
                    }

                    return (
                      <>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-xs font-extrabold uppercase px-2 py-1 rounded border ${verdictColor}`}>
                            {verdict}
                          </span>
                          <span className="text-[11px] text-[var(--ink-2)] leading-tight">{verdictReason}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs mt-3">
                          <div>
                            <div className="text-[10px] text-[var(--ink-3)] uppercase">ARV</div>
                            <div className="font-semibold">{fmtMoney(arv)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-[var(--ink-3)] uppercase">Discount to ARV</div>
                            <div className="font-semibold">{discountPct.toFixed(1)}%</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-[var(--ink-3)] uppercase">Repair Est. ({repairTier})</div>
                            <div className="font-semibold">{fmtMoney(repairEst)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-[var(--ink-3)] uppercase">70% MAO</div>
                            <div className="font-semibold">{fmtMoney(mao)}</div>
                          </div>
                          <div className="col-span-2 pt-1.5 border-t border-[var(--line)]">
                            <div className="text-[10px] text-[var(--ink-3)] uppercase">Spread vs Ask</div>
                            <div className={`font-bold ${spread > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                              {spread > 0 ? "+" : ""}{fmtMoney(spread)}
                            </div>
                          </div>
                        </div>
                        {/* Repair-tier override chips — lets the operator
                            stress-test the verdict against their own scope
                            estimate before adding the deal to pipeline. */}
                        <div
                          className="mt-3 pt-2.5 border-t border-[var(--line)]"
                          data-testid="discover-deal-signal-repair-tier-override"
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)]">
                              Repair scope
                            </div>
                            {!isTierAuto && (
                              <button
                                type="button"
                                onClick={() => setRepairTierOverride(null)}
                                className="text-[10px] text-[var(--ink-3)] underline decoration-dotted hover:text-[var(--ink-1)]"
                                data-testid="discover-deal-signal-tier-reset-btn"
                              >
                                Reset to auto ({age < 15 ? "Light" : age < 40 ? "Medium" : "Heavy"})
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-1.5">
                            {[
                              { tier: "Light",  psf: 15, hint: "cosmetic · paint · flooring" },
                              { tier: "Medium", psf: 25, hint: "kitchen + bath refresh" },
                              { tier: "Heavy",  psf: 40, hint: "full gut · roof · systems" },
                            ].map((opt) => {
                              const isSelected = repairTier === opt.tier;
                              const isAutoPick = isTierAuto && isSelected;
                              return (
                                <button
                                  key={opt.tier}
                                  type="button"
                                  onClick={() => setRepairTierOverride(opt.tier)}
                                  className={`rounded border-2 px-2 py-1.5 text-left transition ${
                                    isSelected
                                      ? "border-[var(--primary,#0F172A)] bg-[var(--primary,#0F172A)]/5"
                                      : "border-[var(--line)] hover:border-[var(--ink-3)]"
                                  }`}
                                  data-testid={`discover-deal-signal-tier-${opt.tier.toLowerCase()}-btn`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="text-[11px] font-bold uppercase tracking-wider">
                                      {opt.tier}
                                    </div>
                                    {isAutoPick && (
                                      <span className="text-[8px] font-bold uppercase text-[var(--ink-3)] tracking-widest">
                                        AUTO
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] text-[var(--ink-2)] mt-0.5">
                                    ${opt.psf}/sqft
                                  </div>
                                  <div className="text-[9px] text-[var(--ink-3)] leading-tight mt-0.5 truncate">
                                    {opt.hint}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          {!isTierAuto && (
                            <div className="text-[10px] text-amber-700 mt-2" data-testid="discover-deal-signal-tier-manual-note">
                              Manual override active — verdict is stress-tested against your scope guess, not the age-based auto tier.
                            </div>
                          )}
                        </div>
                        <div className="text-[10px] text-[var(--ink-3)] mt-2 italic">
                          Quick estimate. Full report adds verified comps, AI verdict, repair scope, and a branded share link.
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {details?.rent && (
                <div className="card p-3" data-testid="discover-detail-rent-avm">
                  <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide">Rent AVM</div>
                  <div className="font-bold text-lg mt-1">{fmtMoney(details.rent.rent)}/mo</div>
                  <div className="text-xs text-[var(--ink-2)]">
                    Range: {fmtMoney(details.rent.rentRangeLow)} – {fmtMoney(details.rent.rentRangeHigh)}
                  </div>
                </div>
              )}

              {details?.property?.owner && (details.property.owner.names?.length || details.property.owner.mailing_address) && (
                <div className="card p-3" data-testid="discover-detail-owner">
                  <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide">Owner</div>
                  {details.property.owner.names?.length > 0 && (
                    <div className="font-semibold mt-1">{details.property.owner.names.join(", ")}</div>
                  )}
                  {details.property.owner.mailing_address && (
                    <div className="text-xs text-[var(--ink-2)] mt-1">
                      Mailing: {details.property.owner.mailing_address}
                      {details.property.owner.absentee && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold">ABSENTEE</span>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={runSkipTraceOnOwner}
                    disabled={skipTracing || adding || smsOpening}
                    className="btn btn-ghost w-full mt-3 text-xs"
                    data-testid="discover-skip-trace-owner-btn"
                    title={details?.in_pipeline_lead_id
                      ? "Run skip trace on this lead"
                      : "Adds to your pipeline then runs skip trace in one click"}
                  >
                    {skipTracing ? <Loader2 size={12} className="animate-spin" /> : <Phone size={12} />}
                    {skipTracing ? "Tracing…" : "Skip trace this owner"}
                  </button>
                  <button
                    type="button"
                    onClick={openTextSellerModal}
                    disabled={smsOpening || skipTracing || adding}
                    className="btn btn-primary w-full mt-2 text-xs"
                    data-testid="discover-text-seller-btn"
                    title="Send a personalized outreach text via your Twilio number"
                  >
                    {smsOpening ? <Loader2 size={12} className="animate-spin" /> : <MessageCircle size={12} />}
                    {smsOpening ? "Preparing…" : "Text this seller"}
                  </button>
                </div>
              )}

              {details?.comparables?.length > 0 && (
                <div className="card p-3" data-testid="discover-detail-comps">
                  <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide mb-2">Comps ({details.comparables.length})</div>
                  <div className="flex flex-col gap-1 text-xs max-h-40 overflow-y-auto">
                    {details.comparables.slice(0, 6).map((c, i) => (
                      <div key={c.id || i} className="flex justify-between gap-2">
                        <span className="truncate">{c.formattedAddress || c.address}</span>
                        <span className="font-semibold shrink-0">{fmtMoney(c.price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={addToPipeline}
                disabled={adding}
                className="btn btn-primary w-full"
                data-testid="discover-add-to-pipeline-btn"
              >
                {adding ? <Loader2 size={14} className="animate-spin" /> : <TrendingUp size={14} />}
                {details?.in_pipeline_lead_id ? "Open Full Report →" : "Run Full Report →"}
              </button>
              {!details?.in_pipeline_lead_id && (
                <div className="text-[10px] text-[var(--ink-3)] text-center mt-1.5 leading-tight">
                  Saves to your Pipeline so you can share, skip-trace, and run AI Coach on it.
                </div>
              )}
            </div>
          </aside>
        </div>
      )}

      {smsModal && (
        <div
          className="fixed inset-0 z-[1100] bg-[rgba(11,29,59,0.55)] backdrop-blur-sm flex items-end md:items-center justify-center p-3"
          onClick={() => !smsModal.sending && setSmsModal(null)}
          data-testid="discover-sms-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--ink-3)]">Text this seller</div>
                <div className="font-display font-bold text-lg leading-tight">
                  {active?.address}
                </div>
                {details?.property?.owner?.names?.[0] && (
                  <div className="text-xs text-[var(--ink-2)] mt-0.5">
                    Owner: {details.property.owner.names[0]}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => !smsModal.sending && setSmsModal(null)}
                disabled={smsModal.sending}
                className="btn btn-ghost p-1.5"
                aria-label="Close"
                data-testid="discover-sms-modal-close"
              >
                <X size={14} />
              </button>
            </div>

            <label className="block text-[11px] font-semibold text-[var(--ink-2)] mb-1">To</label>
            <select
              className="input mb-3"
              value={smsModal.chosenTo}
              onChange={(e) => setSmsModal((m) => ({ ...m, chosenTo: e.target.value }))}
              disabled={smsModal.sending || smsModal.phones.length === 1}
              data-testid="discover-sms-to-select"
            >
              {smsModal.phones.map((p) => {
                const conf = Math.round((p.confidence || 0) * 100);
                const label = `${p.number} · ${p.label || "Phone"} · ${conf}% conf.`;
                return (
                  <option key={p.number} value={p.number}>{label}</option>
                );
              })}
            </select>

            <label className="block text-[11px] font-semibold text-[var(--ink-2)] mb-1">
              Message <span className="text-[var(--ink-3)] font-normal">({smsModal.body.length}/1600)</span>
            </label>
            <textarea
              className="input"
              rows={5}
              value={smsModal.body}
              maxLength={1600}
              onChange={(e) => setSmsModal((m) => ({ ...m, body: e.target.value }))}
              disabled={smsModal.sending}
              data-testid="discover-sms-body"
            />

            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2 text-[10px] text-amber-900 leading-tight">
              <b>TCPA reminder:</b> only text sellers you have a prior business relationship with or reasonable belief of consent. Keep an opt-out (&ldquo;Reply STOP&rdquo;) in your message.
            </div>

            <button
              type="button"
              onClick={sendSellerSms}
              disabled={smsModal.sending || !smsModal.body.trim() || !smsModal.chosenTo}
              className="btn btn-primary w-full mt-3"
              data-testid="discover-sms-send-btn"
            >
              {smsModal.sending ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
              {smsModal.sending ? "Sending…" : "Send text"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function DistressChip({ active, locked, onClick, icon: Icon, label, testid, activeBg, activeText, activeBorder }) {
  const base = "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold border transition";
  const inactive = "bg-white border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--ink-2)]";
  const lockedStyle = "bg-white border-dashed border-amber-200 text-amber-700 opacity-80 cursor-help";
  const activeStyle = `${activeBg} ${activeBorder} ${activeText}`;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${locked ? lockedStyle : active ? activeStyle : inactive}`}
      data-testid={testid}
      aria-pressed={active}
      title={locked ? "Hustler-tier feature" : label}
    >
      <Icon size={11} />
      {label}
    </button>
  );
}
